/**
 * Iter 86.4 / Phase E6 Sprint 2A — Desktop Activity Dashboard (D43).
 *
 * Real-time presence dashboard for the Desktop Activity Agent. Read-only.
 * Refreshes every 30 seconds.
 *
 * Governing principle: Desktop Agent v1 is a presence-monitoring tool,
 * not a surveillance tool.
 *
 * Scope:
 *   • workforce:read or workforce:agent_admin → org-wide
 *   • otherwise → direct reports only
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ArrowsClockwise, CircleNotch, Info, MagnifyingGlass,
    User, Desktop, ClockClockwise, CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import HistoricalSparkline from "../../components/workforce/HistoricalSparkline";
import DailyDrillDown from "../../components/workforce/DailyDrillDown";

const REFRESH_MS = 30_000;
const HISTORICAL_DAYS = 7;
const MAX_DAYS_BACK = 30;

// Iter 89.4 / Sprint 2A.3 — local-date helpers (same shape as ESS).
function todayLocalDateString() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDaysToISO(iso, deltaDays) {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + deltaDays);
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

const OP_BADGE = {
    active: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", dot: "bg-emerald-500", label: "Active" },
    idle:   { bg: "bg-amber-50",   border: "border-amber-300",   text: "text-amber-800",   dot: "bg-amber-500",   label: "Idle" },
    // Iter 86.27 — new headline state when the latest 5-min window is
    // dominated by screen-locked time. Distinct neutral tone so the
    // operator can tell at-a-glance that the machine is locked-but-running
    // vs idle (locked = workstation explicitly secured by the user).
    locked: { bg: "bg-indigo-50",  border: "border-indigo-300",  text: "text-indigo-800",  dot: "bg-indigo-500",  label: "Locked" },
    offline:{ bg: "bg-slate-50",   border: "border-slate-300",   text: "text-slate-700",   dot: "bg-slate-400",   label: "Offline" },
};

const HB_LABEL = {
    online: "Online",
    stale: "Stale",
    offline: "Offline",
    paused: "Paused",
    revoked: "Revoked",
    pending: "Pending",
    never: "Never seen",
};

function humanAgo(iso) {
    if (!iso) return "—";
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return "—";
    const diffSec = Math.round((Date.now() - ts) / 1000);
    if (diffSec < 5) return "just now";
    if (diffSec < 60) return `${diffSec}s ago`;
    const m = Math.round(diffSec / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ${m % 60}m ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
}

function fmtSecs(s) {
    if (s == null) return "—";
    const total = Number(s) || 0;
    if (total === 0) return "0m";
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    if (h === 0) return `${m}m`;
    return `${h}h ${String(m).padStart(2, "0")}m`;
}

function StatusBucket({ count, label, tone, testid }) {
    return (
        <div
            data-testid={testid}
            className={`flex-1 rounded-lg border ${tone.border} ${tone.bg} px-5 py-4`}
        >
            <div className="flex items-center gap-2 mb-1">
                <span className={`inline-block h-2 w-2 rounded-full ${tone.dot}`} />
                <div className={`text-xs uppercase tracking-wide font-semibold ${tone.text}`}>{label}</div>
            </div>
            <div className={`text-3xl font-bold tabular-nums ${tone.text}`}>{count}</div>
        </div>
    );
}

export default function DesktopActivityDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [lastLoadedAt, setLastLoadedAt] = useState(null);
    // Iter 89.4 / Sprint 2A.3 — historical state, all flag-gated and
    // self-hides when the backend returns 404 (flag off).
    const [date, setDate] = useState(todayLocalDateString);
    const [historicalEnabled, setHistoricalEnabled] = useState(false);
    const [historicalByReg, setHistoricalByReg] = useState({});
    const [drilldownRow, setDrilldownRow] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.set("search", search.trim());
            if (statusFilter) params.set("status", statusFilter);
            // Iter 89.4 — only send `date` when the operator has navigated
            // away from today. Backend silently drops it if the flag is off.
            const todayISO = todayLocalDateString();
            if (date && date !== todayISO) params.set("date", date);
            const { data: resp } = await api.get(
                `/workforce/desktop-activity/dashboard?${params.toString()}`
            );
            setData(resp);
            setLastLoadedAt(new Date());
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load desktop activity dashboard"));
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, date]);

    const loadHistorical = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            params.set("days", String(HISTORICAL_DAYS));
            params.set("end_date", date);
            const { data: resp } = await api.get(
                `/workforce/desktop-activity/dashboard/historical?${params.toString()}`
            );
            const byReg = {};
            for (const r of resp?.registrations || []) {
                byReg[r.registration_id] = r.daily_series || [];
            }
            setHistoricalByReg(byReg);
            setHistoricalEnabled(true);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404) {
                // Flag OFF — silently hide the sparkline column and date picker.
                setHistoricalEnabled(false);
                setHistoricalByReg({});
            } else {
                toast.error(formatApiError(err, "Failed to load historical view"));
            }
        }
    }, [date]);

    useEffect(() => {
        Promise.resolve().then(() => { load(); });
    }, [load]);
    useEffect(() => {
        Promise.resolve().then(() => { loadHistorical(); });
    }, [loadHistorical]);
    // Page-load + every 30s thereafter — D43-R2-C.
    useEffect(() => {
        const t = setInterval(() => { Promise.resolve().then(() => { load(); }); }, REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    const rows = data?.rows || [];
    const opCounts = data?.operational_counts || { active: 0, idle: 0, offline: 0 };
    const hbCounts = data?.heartbeat_counts || {};

    const todayISO = useMemo(todayLocalDateString, []);
    const earliestISO = useMemo(() => addDaysToISO(todayISO, -MAX_DAYS_BACK), [todayISO]);
    const canGoPrev = date > earliestISO;
    const canGoNext = date < todayISO;
    const isToday = date === todayISO;
    // Header label varies — "Active Today" when on today, "Active" otherwise.
    const todayColumnSuffix = isToday ? " Today" : "";

    return (
        <div data-testid="desktop-activity-dashboard">
            <PageHeader
                eyebrow="Workforce · Desktop Activity"
                title="Desktop Activity"
                description="Real-time presence dashboard for the Desktop Activity Agent. Read-only · refreshes every 30 seconds."
                actions={(
                    <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2" data-testid="refresh-btn">
                        {loading ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
                        Refresh
                    </Button>
                )}
            />

            <div className="px-6 sm:px-8 py-6 space-y-6 max-w-7xl">

                {/* Governing principle banner */}
                <div
                    className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3"
                    data-testid="presence-not-surveillance-banner"
                >
                    <Info size={20} weight="fill" className="text-blue-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0 text-xs text-blue-900 leading-relaxed">
                        <span className="font-semibold">Presence-monitoring tool, not a surveillance tool.</span>
                        {" "}This dashboard shows aggregated 5-minute presence signals only —
                        never screen content, keystrokes, clipboard, webcam, microphone, URLs, application titles, or files.
                    </div>
                </div>

                {/* Headline: Active / Idle / Locked / Offline */}
                <div className="flex flex-wrap gap-4">
                    <StatusBucket count={opCounts.active || 0} label="Active" tone={OP_BADGE.active} testid="op-count-active" />
                    <StatusBucket count={opCounts.idle || 0} label="Idle" tone={OP_BADGE.idle} testid="op-count-idle" />
                    <StatusBucket count={opCounts.locked || 0} label="Locked" tone={OP_BADGE.locked} testid="op-count-locked" />
                    <StatusBucket count={opCounts.offline || 0} label="Offline" tone={OP_BADGE.offline} testid="op-count-offline" />
                    <div className="flex-1 min-w-[180px] rounded-lg border border-slate-200 bg-white px-5 py-4" data-testid="op-count-total">
                        <div className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-1">Total Registered</div>
                        <div className="text-3xl font-bold tabular-nums text-slate-800">{data?.total || 0}</div>
                        <div className="text-[11px] text-slate-500 mt-1">
                            {Object.entries(hbCounts).filter(([, n]) => n > 0).map(([k, n]) => `${HB_LABEL[k] || k}: ${n}`).join(" · ") || "no devices"}
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[260px]">
                        <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Search employee / machine</label>
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name or machine"
                                className="h-9 pl-9"
                                data-testid="dashboard-search-input"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                            data-testid="dashboard-status-filter"
                        >
                            <option value="">All statuses</option>
                            <option value="active">Active</option>
                            <option value="idle">Idle</option>
                            <option value="offline">Offline</option>
                            <option value="paused">Paused</option>
                            <option value="revoked">Revoked</option>
                            <option value="pending">Pending</option>
                        </select>
                    </div>
                    <div className="text-[11px] text-slate-500 self-center">
                        {lastLoadedAt ? `Last refreshed ${humanAgo(lastLoadedAt.toISOString())}` : ""}
                    </div>
                    {/* Iter 89.4 / Sprint 2A.3 — flag-gated date picker. */}
                    {historicalEnabled && (
                        <div className="flex items-end gap-2" data-testid="dashboard-date-picker-block">
                            <div>
                                <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Date</label>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={!canGoPrev}
                                        onClick={() => setDate((d) => addDaysToISO(d, -1))}
                                        className="gap-1.5 h-9"
                                        data-testid="dashboard-prev-day"
                                        aria-label="Previous day"
                                    >
                                        <CaretLeft size={14} />
                                    </Button>
                                    <input
                                        type="date"
                                        value={date}
                                        min={earliestISO}
                                        max={todayISO}
                                        onChange={(e) => {
                                            const next = e.target.value;
                                            if (next) setDate(next);
                                        }}
                                        className="h-9 px-2 rounded border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                        data-testid="dashboard-date-input"
                                    />
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={!canGoNext}
                                        onClick={() => setDate((d) => addDaysToISO(d, 1))}
                                        className="gap-1.5 h-9"
                                        data-testid="dashboard-next-day"
                                        aria-label="Next day"
                                    >
                                        <CaretRight size={14} />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Table */}
                <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                            <tr>
                                <th className="text-left px-4 py-2 font-semibold">Employee</th>
                                <th className="text-left px-4 py-2 font-semibold">Machine</th>
                                <th className="text-left px-4 py-2 font-semibold">Status</th>
                                <th className="text-left px-4 py-2 font-semibold">Heartbeat</th>
                                <th className="text-left px-4 py-2 font-semibold">Last Seen</th>
                                <th className="text-right px-4 py-2 font-semibold">{`Active${todayColumnSuffix}`}</th>
                                <th className="text-right px-4 py-2 font-semibold">{`Idle${todayColumnSuffix}`}</th>
                                <th className="text-right px-4 py-2 font-semibold" data-testid="locked-today-header">{`Locked${todayColumnSuffix}`}</th>
                                {historicalEnabled && (
                                    <th
                                        className="text-left px-4 py-2 font-semibold"
                                        data-testid="dashboard-sparkline-header"
                                        title={`Active hours · last ${HISTORICAL_DAYS} days`}
                                    >
                                        {HISTORICAL_DAYS}d
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={historicalEnabled ? 9 : 8} className="px-4 py-10 text-center text-slate-400 text-sm" data-testid="dashboard-empty">
                                        {loading ? "Loading…" : "No registered devices in scope."}
                                    </td>
                                </tr>
                            ) : rows.map((r) => {
                                const op = OP_BADGE[r.operational_status] || OP_BADGE.offline;
                                const sparkSeries = historicalByReg[r.registration_id] || [];
                                const isClickable = historicalEnabled;
                                return (
                                    <tr
                                        key={r.registration_id}
                                        data-testid={`dashboard-row-${r.registration_id}`}
                                        className={`hover:bg-slate-50 ${isClickable ? "cursor-pointer" : ""}`}
                                        onClick={isClickable ? () => setDrilldownRow(r) : undefined}
                                    >
                                        <td className="px-4 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <User size={14} className="text-slate-400" />
                                                <span className="font-medium text-slate-800">{r.employee_name || r.employee_id}</span>
                                            </div>
                                            {r.designation && <div className="text-[11px] text-slate-500 pl-6">{r.designation}{r.department ? ` · ${r.department}` : ""}</div>}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex items-center gap-2 font-mono text-[12px] text-slate-700">
                                                <Desktop size={13} className="text-slate-400" />
                                                {r.machine_name || "—"}
                                            </div>
                                            {r.agent_version && <div className="text-[10px] text-slate-400 pl-6">v{r.agent_version}</div>}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${op.border} ${op.bg} ${op.text} text-[11px] font-semibold`}>
                                                <span className={`inline-block h-1.5 w-1.5 rounded-full ${op.dot}`} />
                                                {op.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className="text-[11px] text-slate-600">{HB_LABEL[r.heartbeat_status] || r.heartbeat_status}</span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex items-center gap-1.5 text-[12px] text-slate-600">
                                                <ClockClockwise size={12} className="text-slate-400" />
                                                {humanAgo(r.last_handshake_at)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-emerald-700" data-testid={`active-today-${r.registration_id}`}>{fmtSecs(r.active_today_seconds)}</td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-amber-700" data-testid={`idle-today-${r.registration_id}`}>{fmtSecs(r.idle_today_seconds)}</td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-500" data-testid={`locked-today-${r.registration_id}`}>{fmtSecs(r.locked_today_seconds)}</td>
                                        {historicalEnabled && (
                                            <td className="px-4 py-2.5">
                                                <HistoricalSparkline
                                                    series={sparkSeries}
                                                    testId={`sparkline-${r.registration_id}`}
                                                />
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="text-[11px] text-slate-400">
                    Scope: <span className="font-mono">{data?.scope || "—"}</span> · {data?.total || 0} registered devices · {opCounts.active || 0} active · {opCounts.idle || 0} idle · {opCounts.offline || 0} offline
                </div>
            </div>

            {/* Iter 89.4 / Sprint 2A.3 — Drill-down modal. Rendered only
               when a row is clicked AND the historical (flag-gated) surface
               is enabled — so the modal can never be triggered without the
               operator first enabling the timeline feature. */}
            {historicalEnabled && (
                <DailyDrillDown
                    open={!!drilldownRow}
                    onClose={() => setDrilldownRow(null)}
                    row={drilldownRow}
                />
            )}
        </div>
    );
}
